import { Tooltip } from "../tooltip/Tooltip";
import { cn } from "../../utils";
import { Fragment } from "react";

interface DisplayContentProps {
  multiSelect: boolean;
  showOnlyPlaceholder: boolean;
  placeholder?: string;
  value?: string | string[];
  selectedValues: string[];
  showApplyButton: boolean;
  getLabelForValue: (value: string) => string;
}

const DisplayContent = ({
  multiSelect,
  showOnlyPlaceholder,
  placeholder,
  value,
  selectedValues,
  showApplyButton,
  getLabelForValue,
}: DisplayContentProps) => {
  if (showOnlyPlaceholder) {
    return <div className="al-truncate">{placeholder}</div>;
  }

  if (!multiSelect) {
    // Single select mode
    const label = value ? getLabelForValue(value as string) : undefined;
    return <div className="al-truncate">{label || placeholder || "Select item"}</div>;
  }

  // Multi-select mode
  // When using apply button, show the committed values (from value prop)
  // Otherwise show the current selection (from selectedValues)
  const displayValues = showApplyButton
    ? Array.isArray(value)
      ? value
      : value
        ? [value as string]
        : []
    : selectedValues;

  if (displayValues.length === 0) {
    return <div className="al-truncate">{placeholder || "All Items"}</div>;
  }

  if (displayValues.length === 1) {
    return <div className="al-truncate">{getLabelForValue(displayValues[0])}</div>;
  }

  // For multiple selections, show first 2 items and +n more
  const firstTwoLabels = displayValues.slice(0, 2).map(val => getLabelForValue(val));
  const remainingCount = displayValues.length - 2;
  const allLabels = displayValues.map(val => getLabelForValue(val));

  return (
    <div className="al-flex al-items-center al-gap-none al-min-w-0">
      {firstTwoLabels.map((label, index) => (
        <Fragment key={`${displayValues[index]}-${index}`}>
          <div
            className={cn("al-truncate al-inline-block", index === 0 ? "" : "al-ml-1")}
            style={{ maxWidth: "150px" }}
            title={label}
          >
            {label}
          </div>
          {index < firstTwoLabels.length - 1 && ","}
        </Fragment>
      ))}
      {remainingCount > 0 && (
        <>
          {","}
          <Tooltip
            title={
              <div className="al-max-w-xs">
                <div className="al-font-medium al-mb-1">Selected items:</div>
                <div className="al-space-y-1">
                  {allLabels.map((label, index) => (
                    <div key={`tooltip-${displayValues[index]}-${index}`} className="al-text-sm">
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <span className="al-cursor-help al-text-muted-foreground al-hover:text-foreground al-transition-colors al-ml-1">
              +{remainingCount} more
            </span>
          </Tooltip>
        </>
      )}
    </div>
  );
};

export default DisplayContent;
